import React from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SimpleTooltip from '../SimpleTooltip';

export const STEPS = ['schedule', 'privacy', 'review'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const HOLIDAYS = new Set(['1-1','1-2','5-1','6-12','10-1','12-25','12-26','12-31','7-4','11-11']);

export const getStepStyle = (i, currentStep) => {
  if (i < currentStep) return 'bg-emerald-600 text-white';
  if (i === currentStep) return 'border-2 border-emerald-600 text-emerald-600';
  return 'border-2 border-slate-200';
};

export const isDateDisabled = (d) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysUntilNextMon = (7 - today.getDay()) % 7 || 7;
  const nextMonday = new Date(today); nextMonday.setDate(today.getDate() + daysUntilNextMon);
  if (d < nextMonday) return true;
  if (d.getDay() === 0 || d.getDay() === 6) return true;
  const maxDate = new Date(today); maxDate.setDate(today.getDate() + 60);
  if (d > maxDate) return true;
  if (HOLIDAYS.has(`${d.getMonth()+1}-${d.getDate()}`)) return true;
  return false;
};

export const OnboardingCalendar = ({ calendarMonth, setCalendarMonth, selectedDate, onDateClick }) => {
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return (
    <div data-testid="onboarding-calendar">
      <div className="flex items-center justify-between mb-3">
        <SimpleTooltip content="Previous month">
          <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
        </SimpleTooltip>
        <span className="font-semibold text-sm">{MONTH_NAMES[month]} {year}</span>
        <SimpleTooltip content="Next month">
          <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </SimpleTooltip>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="text-slate-400 font-medium py-1">{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={`week-${wi}`} className="grid grid-cols-7 gap-1">
          {week.map((day, di) => {
            if (!day) return <div key={`day-${di}`} />;
            const dateObj = new Date(year, month, day);
            const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const disabled = isDateDisabled(dateObj);
            const isSelected = dateStr === selectedDate;
            return (
              <button key={`day-${di}`} disabled={disabled} onClick={() => !disabled && onDateClick(dateStr)}
                className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${disabled ? 'text-slate-200 cursor-not-allowed' : 'hover:bg-emerald-50 cursor-pointer text-slate-700'} ${isSelected ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
                data-testid={`calendar-day-${dateStr}`}>{day}</button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
